import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  CircularProgress,
  Button,
  Fab,
  Collapse,
  InputLabel,
} from "@material-ui/core";
import {
  Edit,
  Save,
  Delete,
  Refresh,
  Add,
  CheckCircleOutline,
  CancelOutlined,
} from "@material-ui/icons";
import MainAnimation from "@components/MainAnimation";
import dashboardService from "@services/dashboardService";

function ICEReportsPanel() {
  const [loading, setLoading] = useState(true);
  const [iceReports, setIceReports] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editedReport, setEditedReport] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newReport, setNewReport] = useState(getNewReport());

  function getNewReport() {
    return {
      totalBagsCertified: 0,
      bagsPendingGrading: 0,
      bagsPassedGrading: 0,
      bagsFailedGrading: 0,
      totalBagsRobusta: 0,
      priceArabica: 0,
      confirmed: 0,
      createdAt: new Date().toISOString(),
    };
  }

  const fetchReports = () => {
    setLoading(true);
    dashboardService
      .getIceReports()
      .then((res) => setIceReports(res))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleEdit = (index) => {
    setEditIndex(index);
    setEditedReport({ ...iceReports[index] });
  };

  const handleChange = (field, value) => {
    setEditedReport((prev) => ({
      ...prev,
      [field]: Number(value),
    }));
  };

  // Toggle confirmed state while editing
  const toggleConfirmed = () => {
    setEditedReport((prev) => ({
      ...prev,
      confirmed: prev.confirmed === 1 ? 0 : 1,
    }));
  };

  const handleSave = () => {
    dashboardService
      .updateIceReport(editedReport._id, editedReport)
      .then(() => {
        fetchReports();
        setEditIndex(null);
      });
  };

  // UPDATED: Add confirmation dialog before delete
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this report?")) {
      dashboardService.deleteIceReport(id).then(fetchReports);
    }
  };

  const handleNewChange = (field, value) => {
    setNewReport((prev) => ({
      ...prev,
      [field]: Number(value),
    }));
  };

  // Toggle confirmed state for new report
  const toggleNewConfirmed = () => {
    setNewReport((prev) => ({
      ...prev,
      confirmed: prev.confirmed === 1 ? 0 : 1,
    }));
  };

  const handleCreate = () => {
    dashboardService.createIceReport(newReport).then(() => {
      fetchReports();
      setNewReport(getNewReport());
      setShowCreateForm(false);
    });
  };

  return (
    <MainAnimation>
      <Container maxWidth="md" style={{ paddingTop: 10 }}>
        <Paper style={{ padding: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <h2 style={{ margin: 0, fontSize: "1rem" }}>ICE Reports</h2>
            <IconButton
              size="small"
              onClick={fetchReports}
              style={{ marginRight: 60 }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </div>

          <div style={{ position: "relative" }}>
            <Fab
              color="primary"
              size="small"
              style={{ position: "absolute", top: -30, right: 0 }}
              onClick={() => setShowCreateForm((prev) => !prev)}
            >
              <Add fontSize="small" />
            </Fab>
          </div>

          <Collapse in={showCreateForm}>
            <Paper elevation={1} style={{ padding: 8, marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {[
                  "totalBagsCertified",
                  "bagsPendingGrading",
                  "bagsPassedGrading",
                  "bagsFailedGrading",
                  "totalBagsRobusta",
                  "priceArabica",
                ].map((field) => (
                  <TextField
                    key={field}
                    label={field}
                    value={newReport[field]}
                    type="number"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => handleNewChange(field, e.target.value)}
                    style={{ width: 100 }}
                  />
                ))}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 120,
                    cursor: "pointer",
                  }}
                  onClick={toggleNewConfirmed}
                  title="Toggle Confirmed"
                >
                  <InputLabel shrink style={{ marginRight: 8 }}>
                    Confirmed
                  </InputLabel>
                  {newReport.confirmed === 1 ? (
                    <CheckCircleOutline
                      style={{ color: "green", fontSize: 28 }}
                    />
                  ) : (
                    <CancelOutlined style={{ color: "red", fontSize: 28 }} />
                  )}
                </div>

                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleCreate}
                  style={{ height: 36 }}
                >
                  Save
                </Button>
              </div>
            </Paper>
          </Collapse>

          {loading ? (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 20 }}
            >
              <CircularProgress size={20} />
            </div>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Certified",
                      "Pending",
                      "Passed",
                      "Failed",
                      "Robusta",
                      "Price",
                      "Confirmed",
                      "Date",
                      "Actions",
                    ].map((col) => (
                      <TableCell
                        key={col}
                        style={{ padding: 4, fontSize: "0.75rem" }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {iceReports.map((report, index) => (
                    <TableRow key={report._id}>
                      {editIndex === index ? (
                        <>
                          {[
                            "totalBagsCertified",
                            "bagsPendingGrading",
                            "bagsPassedGrading",
                            "bagsFailedGrading",
                            "totalBagsRobusta",
                            "priceArabica",
                          ].map((field) => (
                            <TableCell key={field} style={{ padding: 4 }}>
                              <TextField
                                value={editedReport[field]}
                                onChange={(e) =>
                                  handleChange(field, e.target.value)
                                }
                                type="number"
                                size="small"
                                style={{ width: 60 }}
                              />
                            </TableCell>
                          ))}

                          <TableCell
                            style={{
                              padding: 4,
                              cursor: "pointer",
                              textAlign: "center",
                            }}
                            onClick={toggleConfirmed}
                            title="Toggle Confirmed"
                          >
                            {editedReport.confirmed === 1 ? (
                              <CheckCircleOutline
                                style={{ color: "green", fontSize: 24 }}
                              />
                            ) : (
                              <CancelOutlined
                                style={{ color: "red", fontSize: 24 }}
                              />
                            )}
                          </TableCell>

                          <TableCell style={{ padding: 4 }}>
                            {new Date(report.createdAt).toLocaleDateString()}
                          </TableCell>

                          <TableCell style={{ padding: 4 }}>
                            <IconButton size="small" onClick={handleSave}>
                              <Save fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          {[
                            "totalBagsCertified",
                            "bagsPendingGrading",
                            "bagsPassedGrading",
                            "bagsFailedGrading",
                            "totalBagsRobusta",
                            "priceArabica",
                          ].map((field) => (
                            <TableCell
                              key={field}
                              style={{ padding: 4, fontSize: "0.75rem" }}
                            >
                              {report[field]}
                            </TableCell>
                          ))}

                          <TableCell
                            style={{
                              padding: 4,
                              fontSize: "0.75rem",
                              textAlign: "center",
                            }}
                          >
                            {report.confirmed === 1 ? (
                              <CheckCircleOutline
                                style={{ color: "green", fontSize: 20 }}
                              />
                            ) : (
                              <CancelOutlined
                                style={{ color: "red", fontSize: 20 }}
                              />
                            )}
                          </TableCell>

                          <TableCell
                            style={{ padding: 4, fontSize: "0.75rem" }}
                          >
                            {new Date(report.createdAt).toLocaleDateString()}
                          </TableCell>

                          <TableCell style={{ padding: 4 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(index)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(report._id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </MainAnimation>
  );
}

export default ICEReportsPanel;
